/**
 * Listas paginadas por número de página (`PageNumberPagination` do backend, 50 por página).
 *
 * Serve às listas pequenas que a tela precisa inteiras: a equipe e os convites de uma empresa
 * vivem do cache persistido e a tela de um vínculo procura a linha nelas, então ler só a
 * primeira página cortaria a lista calada. As listas grandes (notas) usam cursor e scroll.
 */
import { apiClient } from '~/api/client';

interface Page<Row> {
  next?: string | null;
  results: Row[];
}

type QueryParams = Record<string, number | string>;

const FIRST_PAGE = 1;
/** Trava contra um `next` que nunca acaba: 20 páginas são mil linhas, longe de qualquer empresa. */
const MAX_PAGES = 20;

async function collectFrom<Row>(
  url: string,
  params: QueryParams,
  page: number,
  collected: Row[],
): Promise<Row[]> {
  const response = await apiClient.get<Page<Row>>(url, { params: { ...params, page } });
  const rows = [...collected, ...response.data.results];
  if (!response.data.next || page >= MAX_PAGES) return rows;
  return collectFrom(url, params, page + 1, rows);
}

/** Todas as linhas da lista, página a página, na ordem do backend. */
export function fetchAllPages<Row>(url: string, params: QueryParams): Promise<Row[]> {
  return collectFrom<Row>(url, params, FIRST_PAGE, []);
}
